import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { API, graphqlOperation } from 'aws-amplify';
import { ConfirmationModal } from './ConfirmationModal';
import { generatePKCEPair } from './utils/pkce';
import { getEagleViewConfig } from './utils/eagleviewConfig';
import {
  generateRandomState,
  buildAuthorizationUrl,
  SESSION_KEY_STATE,
  SESSION_KEY_CODE_VERIFIER,
} from './utils/oauthFlow';

const disconnectEagleViewMutation = /* GraphQL */ `
  mutation DisconnectEagleView($userID: String!) {
    disconnectEagleView(userID: $userID) {
      success
      message
    }
  }
`;

/**
 * EagleViewSetup component — displays EagleView connection status
 * and provides connect/disconnect actions.
 *
 * Unlike AccuLynx/Leap (org-level API key integrations), EagleView
 * is a per-user OAuth 2.0 connection using Authorization Code + PKCE.
 */
export const EagleViewSetup: React.FC = () => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDisconnecting, setIsDisconnecting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] =
    useState<boolean>(false);

  // Check connection status on mount
  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const me = (window as any).me;
      if (!me?.userID) {
        setIsConnected(false);
        return;
      }
      // TODO: Query backend for EagleView connection status once the
      // GraphQL query is available. For now, default to disconnected.
      setIsConnected(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to check connection status');
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setError(null);

      // Generate PKCE pair (code verifier + code challenge)
      const { codeVerifier, codeChallenge } = await generatePKCEPair();

      // Generate random state for CSRF protection
      const state = generateRandomState();

      // Store state and code verifier in sessionStorage for use during callback
      sessionStorage.setItem(SESSION_KEY_STATE, state);
      sessionStorage.setItem(SESSION_KEY_CODE_VERIFIER, codeVerifier);

      // Get OAuth configuration from environment variables
      const config = getEagleViewConfig();

      // Build the authorization URL with all required parameters
      const authUrl = buildAuthorizationUrl(config, state, codeChallenge);

      // Redirect the browser to EagleView's authorization endpoint
      window.location.href = authUrl;
    } catch (err: any) {
      setError(err?.message || 'Failed to initiate EagleView connection');
    }
  };

  const handleDisconnect = () => {
    setShowDisconnectConfirm(true);
  };

  const confirmDisconnect = async () => {
    setShowDisconnectConfirm(false);
    setError(null);
    setIsDisconnecting(true);

    try {
      const me = (window as any).me;
      if (!me?.userID) {
        throw new Error('User information not available');
      }

      const response: any = await API.graphql(
        graphqlOperation(disconnectEagleViewMutation, {
          userID: me.userID,
        })
      );

      const result = response?.data?.disconnectEagleView;
      if (!result?.success) {
        throw new Error(
          result?.message || 'Failed to disconnect EagleView account'
        );
      }

      setIsConnected(false);
    } catch (err: any) {
      const message =
        err?.errors?.[0]?.message ||
        err?.message ||
        'Failed to disconnect EagleView. Please try again.';
      setError(message);
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <Box sx={{ p: 3, mt: 10, width: '100%' }}>
      <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
          EagleView Integration Setup
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              p: 3,
              backgroundColor: 'background.paper',
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Connection Status
            </Typography>

            {isConnected ? (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <CheckCircleOutlineIcon color="success" />
                  <Typography variant="body1" color="success.main">
                    Connected to EagleView
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Your EagleView account is linked. You can order aerial roof
                  measurement reports.
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDisconnect}
                  disabled={isDisconnecting}
                >
                  {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                </Button>
              </Box>
            ) : (
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Connect your EagleView account to order aerial roof
                  measurement reports directly from the platform.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConnect}
                >
                  Connect EagleView
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <ConfirmationModal
        open={showDisconnectConfirm}
        title="Disconnect EagleView"
        message="Are you sure you want to disconnect your EagleView account? You will need to re-authenticate to use EagleView services again."
        confirmText="Disconnect"
        onConfirm={confirmDisconnect}
        onCancel={() => setShowDisconnectConfirm(false)}
        severity="warning"
      />
    </Box>
  );
};

export default EagleViewSetup;
